import React from "react";
import { twMerge } from "tailwind-merge";
import { type ComponentFormatter, formatComponentValue } from "../../../utils/formatting/component-formatter";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps, CellEmphasis, LinkBehavior } from "../DataTable.types";
import { getCellContainerClasses, getCellTextClasses, getSkeletonSize, resolveValue, wrapWithLink } from "./cellUtils";

export interface FormattedCellProps<T = any> extends CellComponentProps<T> {
  /**
   * Formatter using the centralized formatting system
   * Can be a FieldFormat object or a custom function
   */
  formatter?: ComponentFormatter;

  /**
   * Text alignment
   */
  align?: "left" | "center" | "right";

  /**
   * Visual emphasis
   */
  emphasis?: CellEmphasis;

  /**
   * Make the cell a link
   */
  href?: string | ((row: T) => string);

  /**
   * Link behavior
   */
  linkBehavior?: LinkBehavior;

  /**
   * Additional className for the cell container
   */
  className?: string;

  /**
   * Custom empty text
   */
  emptyText?: React.ReactNode;

  /**
   * Custom empty className
   */
  emptyClassName?: string;
}

/**
 * A universal cell component that uses the centralized formatting system
 * This provides a consistent API with Kpi and StatList components
 *
 * @example
 * ```tsx
 * // In a DataTable column definition
 * {
 *   id: "power",
 *   accessor: "power",
 *   cell: FormattedCell,
 *   cellProps: {
 *     formatter: { type: "power" },
 *     align: "right"
 *   }
 * }
 * ```
 */
export function FormattedCell<T = any>({
  value,
  row,
  context,
  formatter,
  align = "left",
  emphasis = "normal",
  href,
  linkBehavior = "hover",
  className,
  emptyText = "—",
  emptyClassName = "text-text-muted",
}: FormattedCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize(emphasis)} />;
  }

  // Use the centralized formatting system
  const formatted = formatComponentValue({
    value,
    formatter,
    emptyText,
    emptyClassName,
  });

  // Apply text emphasis classes
  const textClasses = getCellTextClasses(emphasis);
  const containerClasses = getCellContainerClasses(align, twMerge(textClasses, className));

  // Wrap in a container div for consistent alignment
  const content = <div className={containerClasses}>{formatted}</div>;

  // If href is provided, wrap with link behavior
  const resolvedHref = resolveValue(href, row);
  if (resolvedHref) {
    return wrapWithLink(content, resolvedHref, row, linkBehavior);
  }

  return content;
}
